import React, { useState } from "react"
import { SyncStatusBadge } from "./SyncStatusBadge"

const money = (cents, currency = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format((cents || 0) / 100)

const formatDate = (iso, never) => {
  if (!iso) return never
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// One discovered app: name + status, mini-stats, inline rename, confirm, resync.
// `onUpdate(id, attrs)` PATCHes; `onResync(id)` POSTs the backfill. Both return
// promises so the card can show pending state.
export function AppCard({ app, copy, detailPath, onUpdate, onResync, onRemove }) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(app.name || "")
  const [slugEditing, setSlugEditing] = useState(false)
  const [slug, setSlug] = useState(app.app_store_slug || "")
  const [slugBusy, setSlugBusy] = useState(false)
  const [busy, setBusy] = useState(false)
  const [resyncing, setResyncing] = useState(false)
  const [removing, setRemoving] = useState(false)

  const save = () => {
    setBusy(true)
    onUpdate(app.id, { name }).finally(() => {
      setBusy(false)
      setEditing(false)
    })
  }

  const saveSlug = () => {
    setSlugBusy(true)
    onUpdate(app.id, { app_store_slug: slug })
      .then(() => setSlugEditing(false))
      .finally(() => setSlugBusy(false))
  }

  const confirm = () => {
    setBusy(true)
    onUpdate(app.id, { confirmed: true }).finally(() => setBusy(false))
  }

  const resync = () => {
    setResyncing(true)
    onResync(app.id).finally(() => setResyncing(false))
  }

  // Only manually-added apps can be removed; discovered apps are sync-owned.
  const remove = () => {
    if (!window.confirm(copy.card.removeConfirm)) return
    setRemoving(true)
    onRemove(app.id).catch(() => setRemoving(false))
  }

  return (
    <section className="flex flex-col rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {editing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
              aria-label={copy.rename}
            />
          ) : (
            <h2 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
              <a href={detailPath} className="hover:underline">{app.name}</a>
            </h2>
          )}
          <p className="mt-0.5 truncate text-xs text-gray-400 dark:text-gray-500">{app.shopify_app_gid}</p>
        </div>
        <SyncStatusBadge status={app.status} label={copy.statuses[app.status]} />
      </div>

      {!app.confirmed && (
        <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          {copy.card.discoveredHint}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-3 gap-3 text-sm">
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">{copy.card.merchants}</dt>
          <dd className="font-semibold text-gray-900 dark:text-gray-100">{app.merchants_count.toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">{copy.card.mrr}</dt>
          <dd className="font-semibold text-gray-900 dark:text-gray-100">{money(app.mrr_cents)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">{copy.card.lastSynced}</dt>
          <dd className="font-medium text-gray-600 dark:text-gray-400">
            {formatDate(app.events_synced_through, copy.card.never)}
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-gray-100 dark:border-gray-800 pt-4">
        <dt className="text-xs text-gray-500 dark:text-gray-400">{copy.card.slugLabel}</dt>
        {slugEditing ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={copy.card.slugPlaceholder}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
              aria-label={copy.card.slugLabel}
            />
            <button
              type="button"
              onClick={saveSlug}
              disabled={slugBusy}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {slugBusy ? copy.card.slugSaving : copy.card.slugSave}
            </button>
            <button
              type="button"
              onClick={() => {
                setSlug(app.app_store_slug || "")
                setSlugEditing(false)
              }}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {copy.card.cancel}
            </button>
          </div>
        ) : (
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-300">
              {app.app_store_slug || copy.card.slugNone}
            </span>
            <button
              type="button"
              onClick={() => setSlugEditing(true)}
              className="shrink-0 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {app.app_store_slug ? copy.card.slugEdit : copy.card.slugSet}
            </button>
          </div>
        )}
        {!app.app_store_slug && !slugEditing && (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{copy.card.slugHint}</p>
        )}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {editing ? (
          <>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {copy.card.save}
            </button>
            <button
              type="button"
              onClick={() => {
                setName(app.name || "")
                setEditing(false)
              }}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {copy.card.cancel}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {copy.card.rename}
          </button>
        )}

        {!app.confirmed && !editing && (
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {copy.card.confirm}
          </button>
        )}
        {app.confirmed && !editing && (
          <span className="inline-flex items-center text-sm font-medium text-green-700 dark:text-green-300">
            {copy.card.confirmed}
          </span>
        )}

        <button
          type="button"
          onClick={resync}
          disabled={resyncing}
          className="ml-auto rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {resyncing ? copy.card.resyncing : copy.card.resync}
        </button>

        {!app.discovered && !editing && (
          <button
            type="button"
            onClick={remove}
            disabled={removing}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 disabled:opacity-50"
          >
            {removing ? copy.card.removing : copy.card.remove}
          </button>
        )}
      </div>
    </section>
  )
}
